import React, { useEffect } from 'react';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { ValidationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { useLogger } from '@hooks';
import { saveUserInput, usePromotionalCodeDuration, useServiceResponse } from '@store';
import { useFetchRetrievePromoCodeDetail } from '@services';
import { PromoCodeDetailDto, PromoCodeDetailOutputDto, PromoCodeStatus } from '@api/izibizi-xp';
import { useDispatch } from 'react-redux';

interface CardPromotionalCodeResources {
	title: string;
	buttonValidate: string;
	messageInput: string;
	placeholderInput: string;
	titleSuccess: string;
	subtitleSuccess: string;
	titleOffCampaign: string;
	subtitleOffCampaign: string;
	titleNoCampaign: string;
	subtitleNoCampaign: string;
	titleUsedCode: string;
	subtitleUsedCode: string;
}

type CardPromotionalCodeStatus = 'EXPIRED' | 'INACTIVE' | 'NONE' | 'USED' | 'ACTIVE';
type InputCodeStatus = 'valid' | 'invalid' | 'none';

interface CardPromotionalCodeOutput {
	promotionalCode?: string;
	retrievePromoCodeDetailServiceData?: PromoCodeDetailOutputDto;
	retrievePromoCodeDetail?: PromoCodeDetailOutputDto;
	promotionalCodeStatus: CardPromotionalCodeStatus;
	inputCodeStatus: InputCodeStatus;
	resources: CardPromotionalCodeResources;
	onChange: (value: string) => void;
	onClickClear: () => void;
	onClickValidate: () => void;
	handleOnClickBack: () => void;
	promoCodeWithErrors: boolean;
	savedPromoInputStatus: PromoCodeStatus | undefined;
}

export const useCardPromotionalCodeHelper = (): CardPromotionalCodeOutput => {
	useDidMount(() => {
		setRetrievePromoCodeDetailServiceData(retrievePromoCodeDetail);
		setPromotionalCode(
			retrievePromoCodeDetail?.promoCodeDetail?.code ? retrievePromoCodeDetail?.promoCodeDetail?.code : ''
		);
	});

	const t = useTranslation();
	const logger = useLogger();
	const [promotionalCode, setPromotionalCode] = React.useState<string>('');
	const [promoCodeWithErrors, setPromoCodeWithErrors] = React.useState<boolean>(false);
	const [savedPromoInputStatus, setSavedPromoInputStatus] = React.useState<PromoCodeStatus | undefined>();
	const [retrievePromoCodeDetailServiceData, setRetrievePromoCodeDetailServiceData] = React.useState<
		PromoCodeDetailOutputDto | undefined
	>();
	const promotionalCodeStatus = React.useRef<CardPromotionalCodeStatus>(
		savedPromoInputStatus ? savedPromoInputStatus : 'NONE'
	);
	const inputCodeStatus = React.useRef<InputCodeStatus>('none');
	const { fetch: fetchRetrievePromoCodeDetail } = useFetchRetrievePromoCodeDetail();

	const { retrievePromoCodeDetail } = useServiceResponse();
	const manyMonthsOnDiscount = usePromotionalCodeDuration();
	const dispatch = useDispatch();

	const handleOnChange = React.useCallback(
		(value: string) => {
			const regexValidationCode = new RegExp('^[A-Za-z0-9 ]+$'); // '^[A-Za-z]{3}[0-9]{2}[A-Za-z]$'

			const validation = ValidationHelper.validateRegex(regexValidationCode, '')(value.toLowerCase());
			logger('OnchangeInput>Validation', { validation });

			if (validation === undefined) {
				inputCodeStatus.current = 'none';
			} else {
				inputCodeStatus.current = 'invalid';
			}

			setPromotionalCode(value);
		},
		[logger]
	);

	const handleOnClickClear = React.useCallback(() => {
		setPromotionalCode('');
	}, []);

	const handleOnClickValidate = React.useCallback(() => {
		// call service
		dispatch(saveUserInput({ promotionalCode: promotionalCode }));
		fetchRetrievePromoCodeDetail({
			code: promotionalCode,
		}).then((resp) => {
			// resp.data?.promoCodeStatus === PromoCodeStatus.EXPIRED ||
			// resp.data?.promoCodeStatus === PromoCodeStatus.INACTIVE ||
			// resp.data?.promoCodeStatus === PromoCodeStatus.NONE ||
			// resp.data?.promoCodeStatus === PromoCodeStatus.USED
			// 	? setPromoCodeWithErrors(true)
			// 	: setPromoCodeWithErrors(false);
			switch (resp.data?.promoCodeStatus) {
				case PromoCodeStatus.EXPIRED:
					promotionalCodeStatus.current = 'EXPIRED';
					break;
				case PromoCodeStatus.INACTIVE:
					promotionalCodeStatus.current = 'INACTIVE';
					break;
				case PromoCodeStatus.NONE:
					promotionalCodeStatus.current = 'NONE';
					break;
				case PromoCodeStatus.USED:
					promotionalCodeStatus.current = 'USED';
					break;
				case PromoCodeStatus.ACTIVE:
					promotionalCodeStatus.current = 'ACTIVE';
					break;
			}
			inputCodeStatus.current = 'valid';

			setRetrievePromoCodeDetailServiceData(resp.data as PromoCodeDetailOutputDto);
		});
	}, [dispatch, fetchRetrievePromoCodeDetail, promotionalCode]);

	const handleOnClickBack = React.useCallback(() => {
		promotionalCodeStatus.current = 'NONE';
		inputCodeStatus.current = 'none';
		setPromoCodeWithErrors(false);
		setRetrievePromoCodeDetailServiceData(undefined);
		setPromotionalCode('');
	}, []);

	const resources = React.useMemo((): CardPromotionalCodeResources => {
		return {
			title: t('placeholders.cardPromotionalCode.hasPromotionalCode'),
			buttonValidate: t('general.validate'),
			messageInput: t('placeholders.cardPromotionalCode.promotionalCodeIsInvalid'),
			placeholderInput: t('placeholders.cardPromotionalCode.insertCode'),
			titleSuccess: t('placeholders.cardPromotionalCode.promoCodeIsValid'),
			subtitleSuccess: t('placeholders.cardPromotionalCode.canUsePromotionalCodeForFewMonths', {
				months: manyMonthsOnDiscount,
			}),
			titleNoCampaign: t('placeholders.cardPromotionalCode.promoCodeIsNoCampaign.title'),
			subtitleNoCampaign: t('placeholders.cardPromotionalCode.promoCodeIsNoCampaign.subtitle'),
			titleOffCampaign: t('placeholders.cardPromotionalCode.promoCodeIsOffCampaign.title'),
			subtitleOffCampaign: t('placeholders.cardPromotionalCode.promoCodeIsOffCampaign.subtitle'),
			titleUsedCode: t('placeholders.cardPromotionalCode.promoCodeIsUsed.title'),
			subtitleUsedCode: t('placeholders.cardPromotionalCode.promoCodeIsUsed.subtitle'),
		};
	}, [t, manyMonthsOnDiscount]);

	return {
		resources,
		retrievePromoCodeDetailServiceData,
		retrievePromoCodeDetail,
		promotionalCode,
		inputCodeStatus: inputCodeStatus.current,
		promotionalCodeStatus: promotionalCodeStatus.current,
		onChange: handleOnChange,
		onClickClear: handleOnClickClear,
		onClickValidate: handleOnClickValidate,
		handleOnClickBack,
		promoCodeWithErrors,
		savedPromoInputStatus,
	};
};
